import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from '../../components/Modal'
import Boton from '../../components/Boton'
import CampoFecha from '../../components/CampoFecha'
import MoneyInput from '../../components/MoneyInput'
import { erpApi } from '../../api/erp'
import { fmtFechaHora, fmtFecha } from '../../utils/fecha'
import ModalAltaTercero from './ModalAltaTercero'
import { nuevoUuid, mensajeError, fmtImporte, labelOperacion, useDepositos, renglonesValidos, renglonesPayload, totalRenglones, hoverFila, st } from './_helpers'
import { SelectDeposito, BuscadorTercero, RenglonesEditor, CajaError, CajaOk, Campo, Badge } from './_comun'
import { colors, space } from '../../design/tokens'

const fecha = (s) => (s && String(s).includes('/')) ? s : fmtFecha(s)

/**
 * Compras — pedido, recepción (ingresa stock), factura del
 * proveedor, NC, pago y gasto vario; anulaciones con motivo.
 *
 * La API v1 del ERP NO tiene listado de compras: lo que se ve abajo es la
 * bitácora de LGR (qué se mandó, quién, qué respondió el ERP), no la verdad
 * del ERP. La cuenta corriente del proveedor SÍ es del ERP.
 */
export default function Emp900ComprasPage() {
  const navigate = useNavigate()
  const [registro, setRegistro] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [modal, setModal] = useState(null) // 'pedido' | 'recepcion' | 'factura' | 'nc' | 'pago' | 'gasto' | {anular: fila}

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await erpApi.registro({ area: 'compras' })
      setRegistro(data.registro || []); setError('')
    } catch (e) { setError(mensajeError(e)) } finally { setLoading(false) }
  }, [])
  useEffect(() => { cargar() }, [cargar])

  const cerrar = () => { setModal(null); cargar() }

  return (
    <div>
      <div style={st.header}>
        <h2 style={st.title}>Compras</h2>
        <div style={st.acciones}>
          <Boton variante="secondary" onClick={() => setModal('pedido')}>Pedido</Boton>
          <Boton variante="secondary" onClick={() => setModal('factura')}>Factura proveedor</Boton>
          <Boton variante="secondary" onClick={() => setModal('nc')}>NC proveedor</Boton>
          <Boton variante="secondary" onClick={() => setModal('pago')}>Pago</Boton>
          <Boton variante="secondary" onClick={() => setModal('gasto')}>Gasto</Boton>
          <Boton variante="primary" onClick={() => setModal('recepcion')}>+ Recepción</Boton>
        </div>
      </div>
      <p style={st.sub}>
        Valores finales. Abajo, lo cargado desde LGR (bitácora, con quién lo hizo) — el ERP no expone un listado de compras.
        Para el saldo real del proveedor: <a href="#" style={{ color: colors.link, fontWeight: 500 }} onClick={e => { e.preventDefault(); navigate('/emp900/cuentas?tipo=proveedor') }}>cuenta corriente</a>.
      </p>

      <div style={st.tableWrap}>
        <table style={st.table}>
          <thead><tr>{['Fecha', 'Usuario', 'Operación', 'Número', 'Total', 'Resultado', 'Acción'].map(h => <th key={h} style={{ ...st.th, ...(h === 'Total' ? { textAlign: 'right' } : {}) }}>{h}</th>)}</tr></thead>
          <tbody>
            {loading && registro.length === 0 ? <tr><td colSpan={7} style={st.empty}>Cargando…</td></tr>
              : error ? <tr><td colSpan={7} style={st.empty}>{error}</td></tr>
                : registro.length === 0 ? <tr><td colSpan={7} style={st.empty}>Todavía no se cargó ninguna compra desde LGR.</td></tr>
                  : registro.map(f => {
                    const r = resumenFila(f)
                    return (
                      <tr key={f.id} style={st.tr}>
                        <td style={st.tdFecha}>{fmtFechaHora(f.fecha)}</td>
                        <td style={st.tdSec}>{f.usuario || '—'}</td>
                        <td style={st.td}>{labelOperacion(f.ruta)}</td>
                        <td style={st.tdMono}>{r.numero || '—'}</td>
                        <td style={st.tdImporte}>{r.total != null ? fmtImporte(r.total) : ''}</td>
                        <td style={st.td}>
                          {f.error || (f.status && f.status >= 400)
                            ? <Badge tono="err">{f.error ? 'sin respuesta' : f.status === 422 ? 'rechazado por el ERP' : `error ${f.status}`}</Badge>
                            : f.repetida ? <Badge tono="neutro">repetida</Badge> : <Badge tono="ok">ok</Badge>}
                          {f.status >= 400 && f.respuesta?.message && <div style={{ ...st.info, margin: '2px 0 0' }}>{f.respuesta.message}</div>}
                        </td>
                        <td style={st.td} onClick={e => e.stopPropagation()}>
                          {r.anulable && <Boton variante="destructive" sm onClick={() => setModal({ anular: { ...r, fila: f } })}>Anular</Boton>}
                        </td>
                      </tr>
                    )
                  })}
          </tbody>
        </table>
      </div>

      {modal === 'pedido' && <PedidoModal onClose={cerrar} />}
      {modal === 'recepcion' && <RecepcionModal onClose={cerrar} />}
      {(modal === 'factura' || modal === 'nc') && <FacturaProveedorModal tipo={modal} onClose={cerrar} />}
      {modal === 'pago' && <PagoModal onClose={cerrar} />}
      {modal === 'gasto' && <GastoModal onClose={cerrar} />}
      {modal?.anular && <AnularModal objetivo={modal.anular} onClose={cerrar} />}
    </div>
  )
}

/** Qué mostrar de una fila de bitácora según lo que respondió el ERP. */
function resumenFila(f) {
  const r = f.respuesta || {}
  const p = f.payload || {}
  const ok = !f.error && f.status && f.status < 400
  if (f.ruta.endsWith('/anular')) return { numero: r.recepcion?.numero || r.factura?.numero_proveedor || r.pago?.documento, total: null, anulable: false }
  if (r.pedido) return { numero: r.pedido.numero, total: r.pedido.total, anulable: false }
  if (r.recepcion) return { numero: r.recepcion.numero, total: totalRenglones(p.renglones || [], 'costo'), anulable: ok && r.recepcion.estado !== 'anulada', tipo: 'recepcion', id: r.recepcion.id }
  if (r.factura) return { numero: r.factura.numero_proveedor, total: r.factura.total, anulable: ok && r.factura.estado !== 'anulada', tipo: 'factura', id: r.factura.id }
  if (r.pago) return { numero: r.pago.documento, total: r.pago.importe, anulable: ok, tipo: 'pago', id: r.pago.id }
  return { numero: p.numero_proveedor, total: p.importe ?? (p.renglones ? totalRenglones(p.renglones, p.renglones[0]?.costo != null ? 'costo' : 'precio') : null), anulable: false }
}

// ---------------------------------------------------------------- Pedido

function PedidoModal({ onClose }) {
  const [uuid] = useState(nuevoUuid)
  const [tercero, setTercero] = useState(null)
  const [alta, setAlta] = useState(false)
  const [renglones, setRenglones] = useState([])
  const [fechaDoc, setFechaDoc] = useState('')
  const [entrega, setEntrega] = useState('')
  const [obs, setObs] = useState('')
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)

  const enviar = async () => {
    setError(''); setEnviando(true)
    try {
      const { data } = await erpApi.pedir({ uuid, tercero_id: tercero.id, renglones: renglonesPayload(renglones), fecha: fechaDoc || undefined, fecha_entrega: entrega || undefined, observaciones: obs || undefined })
      setResultado(data)
    } catch (e) { setError(mensajeError(e)) } finally { setEnviando(false) }
  }

  return (
    <Modal title="Pedido a proveedor" onClose={onClose} maxWidth="860px">
      {alta && <ModalAltaTercero rol="proveedor" onClose={() => setAlta(false)} onCreado={t => { setTercero(t); setAlta(false) }} />}
      {resultado ? (
        <div style={st.seccion}>
          <CajaOk titulo={`Pedido ${resultado.pedido?.numero}${resultado.repetida ? ' (ya estaba registrado)' : ''}`}>
            Total {fmtImporte(resultado.pedido?.total)} · estado {resultado.pedido?.estado}. ID {resultado.pedido?.id} — usalo al recibir contra este pedido.
          </CajaOk>
          <div style={st.pie}><Boton variante="ghost" onClick={onClose}>Cerrar</Boton></div>
        </div>
      ) : (
        <div style={st.seccion}>
          <p style={{ ...st.info, margin: 0 }}>El pedido no mueve stock ni cuenta: es lo que se espera recibir. La recepción parcial lo deja abierto; la completa lo cierra.</p>
          <Campo label="Proveedor"><BuscadorTercero value={tercero} onChange={setTercero} rol="proveedor" onNuevo={() => setAlta(true)} /></Campo>
          <div style={st.grid2}>
            <Campo label="Fecha (vacío = hoy)"><CampoFecha style={st.input} value={fechaDoc} onChange={setFechaDoc} /></Campo>
            <Campo label="Fecha de entrega"><CampoFecha style={st.input} value={entrega} onChange={setEntrega} /></Campo>
          </div>
          <Campo label="Renglones"><RenglonesEditor renglones={renglones} onChange={setRenglones} labelImporte="Precio final" /></Campo>
          <Campo label="Observaciones"><textarea style={st.textarea} value={obs} onChange={e => setObs(e.target.value)} maxLength={250} /></Campo>
          <CajaError>{error}</CajaError>
          <div style={st.pie}>
            <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
            <Boton variante="primary" onClick={enviar} disabled={!tercero || !renglonesValidos(renglones) || enviando}>{enviando ? 'Enviando al ERP…' : 'Registrar pedido'}</Boton>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ---------------------------------------------------------------- Recepción

function RecepcionModal({ onClose }) {
  const depositos = useDepositos()
  const [uuid] = useState(nuevoUuid)
  const [tercero, setTercero] = useState(null)
  const [alta, setAlta] = useState(false)
  const [deposito, setDeposito] = useState('')
  const [pedidoId, setPedidoId] = useState('')
  const [remitoProv, setRemitoProv] = useState('')
  const [renglones, setRenglones] = useState([])
  const [fechaDoc, setFechaDoc] = useState('')
  const [obs, setObs] = useState('')
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)

  const enviar = async () => {
    setError(''); setEnviando(true)
    try {
      const { data } = await erpApi.recibir({
        uuid, tercero_id: tercero.id, deposito, renglones: renglonesPayload(renglones, 'costo'),
        pedido_id: pedidoId || undefined, remito_proveedor: remitoProv || undefined, fecha: fechaDoc || undefined, observaciones: obs || undefined,
      })
      setResultado(data)
    } catch (e) { setError(mensajeError(e)) } finally { setEnviando(false) }
  }

  return (
    <Modal title="Recepción de mercadería" onClose={onClose} maxWidth="860px">
      {alta && <ModalAltaTercero rol="proveedor" onClose={() => setAlta(false)} onCreado={t => { setTercero(t); setAlta(false) }} />}
      {resultado ? (
        <div style={st.seccion}>
          <CajaOk titulo={`Recepción ${resultado.recepcion?.numero}${resultado.repetida ? ' (ya estaba registrada)' : ''}`}>
            Ingresó al depósito {deposito} · estado {resultado.recepcion?.estado}. ID {resultado.recepcion?.id} — usalo al facturar contra esta recepción.
          </CajaOk>
          <div style={st.pie}><Boton variante="ghost" onClick={onClose}>Cerrar</Boton></div>
        </div>
      ) : (
        <div style={st.seccion}>
          <p style={{ ...st.info, margin: 0 }}>INGRESA stock al costo final que se carga. Sin pedido también vale (compra de mostrador).</p>
          <Campo label="Proveedor"><BuscadorTercero value={tercero} onChange={setTercero} rol="proveedor" onNuevo={() => setAlta(true)} /></Campo>
          <div style={st.grid3}>
            <Campo label="Depósito"><SelectDeposito value={deposito} onChange={setDeposito} depositos={depositos} /></Campo>
            <Campo label="ID de pedido (opcional)"><input style={st.input} inputMode="numeric" value={pedidoId} onChange={e => setPedidoId(e.target.value.replace(/\D/g, ''))} /></Campo>
            <Campo label="Remito del proveedor"><input style={st.input} value={remitoProv} onChange={e => setRemitoProv(e.target.value)} maxLength={40} /></Campo>
          </div>
          <Campo label="Fecha (vacío = hoy)" style={{ maxWidth: 220 }}><CampoFecha style={st.input} value={fechaDoc} onChange={setFechaDoc} /></Campo>
          <Campo label="Renglones"><RenglonesEditor renglones={renglones} onChange={setRenglones} campoImporte="costo" labelImporte="Costo final" /></Campo>
          <Campo label="Observaciones"><textarea style={st.textarea} value={obs} onChange={e => setObs(e.target.value)} maxLength={250} /></Campo>
          <CajaError>{error}</CajaError>
          <div style={st.pie}>
            <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
            <Boton variante="primary" onClick={enviar} disabled={!tercero || !deposito || !renglonesValidos(renglones, 'costo') || enviando}>{enviando ? 'Enviando al ERP…' : 'Registrar recepción'}</Boton>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ---------------------------------------------------------------- Factura / NC del proveedor

function FacturaProveedorModal({ tipo, onClose }) {
  const esNc = tipo === 'nc'
  const [uuid] = useState(nuevoUuid)
  const [tercero, setTercero] = useState(null)
  const [alta, setAlta] = useState(false)
  const [numero, setNumero] = useState('')
  const [refId, setRefId] = useState('')
  const [renglones, setRenglones] = useState([])
  const [fechaDoc, setFechaDoc] = useState('')
  const [vence, setVence] = useState('')
  const [obs, setObs] = useState('')
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)

  const enviar = async () => {
    setError(''); setEnviando(true)
    const base = { uuid, tercero_id: tercero.id, numero_proveedor: numero.trim(), renglones: renglonesPayload(renglones), fecha: fechaDoc || undefined, observaciones: obs || undefined }
    try {
      const { data } = esNc
        ? await erpApi.notaCreditoCompra({ ...base, factura_id: refId || undefined })
        : await erpApi.facturarCompra({ ...base, recepcion_id: refId || undefined, fecha_vencimiento: vence || undefined })
      setResultado(data)
    } catch (e) { setError(mensajeError(e)) } finally { setEnviando(false) }
  }

  return (
    <Modal title={`${esNc ? 'Nota de crédito' : 'Factura'} del proveedor`} onClose={onClose} maxWidth="860px">
      {alta && <ModalAltaTercero rol="proveedor" onClose={() => setAlta(false)} onCreado={t => { setTercero(t); setAlta(false) }} />}
      {resultado ? (
        <div style={st.seccion}>
          <CajaOk titulo={`${esNc ? 'NC' : 'Factura'} ${resultado.factura?.numero_proveedor} registrada${resultado.repetida ? ' (ya estaba)' : ''}`}>
            Total {fmtImporte(resultado.factura?.total)} · Saldo con el proveedor {fmtImporte(resultado.saldo_proveedor)}. ID {resultado.factura?.id}.
          </CajaOk>
          <div style={st.pie}><Boton variante="ghost" onClick={onClose}>Cerrar</Boton></div>
        </div>
      ) : (
        <div style={st.seccion}>
          <p style={{ ...st.info, margin: 0 }}>El número lo TRAE el proveedor. Repetir el número del mismo proveedor lo rechaza el ERP (salvo anulada). Renglones con artículo o libres.</p>
          <Campo label="Proveedor"><BuscadorTercero value={tercero} onChange={setTercero} rol="proveedor" onNuevo={() => setAlta(true)} /></Campo>
          <div style={st.grid3}>
            <Campo label="Número del proveedor"><input style={st.input} value={numero} onChange={e => setNumero(e.target.value)} maxLength={40} placeholder="A-0001-00001234" /></Campo>
            <Campo label={esNc ? 'ID de factura a imputar (opcional)' : 'ID de recepción (opcional)'}><input style={st.input} inputMode="numeric" value={refId} onChange={e => setRefId(e.target.value.replace(/\D/g, ''))} /></Campo>
            <Campo label="Fecha (vacío = hoy)"><CampoFecha style={st.input} value={fechaDoc} onChange={setFechaDoc} /></Campo>
            {!esNc && <Campo label="Vencimiento"><CampoFecha style={st.input} value={vence} onChange={setVence} /></Campo>}
          </div>
          <Campo label="Renglones"><RenglonesEditor renglones={renglones} onChange={setRenglones} labelImporte="Importe final" permitirLibre /></Campo>
          <Campo label="Observaciones"><textarea style={st.textarea} value={obs} onChange={e => setObs(e.target.value)} maxLength={250} /></Campo>
          <CajaError>{error}</CajaError>
          <div style={st.pie}>
            <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
            <Boton variante="primary" onClick={enviar} disabled={!tercero || !numero.trim() || !renglonesValidos(renglones) || enviando}>{enviando ? 'Enviando al ERP…' : (esNc ? 'Registrar NC' : 'Registrar factura')}</Boton>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ---------------------------------------------------------------- Pago

function PagoModal({ onClose }) {
  const [uuid] = useState(nuevoUuid)
  const [tercero, setTercero] = useState(null)
  const [importe, setImporte] = useState('')
  const [fechaDoc, setFechaDoc] = useState('')
  const [obs, setObs] = useState('')
  const [cuenta, setCuenta] = useState(null)
  const [ids, setIds] = useState(new Set())
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)

  const elegirTercero = (t) => { setTercero(t); setCuenta(null); setIds(new Set()); setError('') }
  useEffect(() => {
    if (!tercero) return
    erpApi.cuenta(tercero.id, 'proveedor').then(({ data }) => setCuenta(data)).catch(e => setError(mensajeError(e)))
  }, [tercero])

  const toggle = (id) => setIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  const enviar = async () => {
    setError(''); setEnviando(true)
    try {
      const { data } = await erpApi.pagar({
        uuid, tercero_id: tercero.id, importe,
        factura_ids: ids.size ? [...ids].map(i => cuenta.pendientes.find(p => p.id === i)?.origen_id).filter(Boolean) : undefined,
        fecha: fechaDoc || undefined, observaciones: obs || undefined,
      })
      setResultado(data)
    } catch (e) { setError(mensajeError(e)) } finally { setEnviando(false) }
  }

  return (
    <Modal title="Pago a proveedor" onClose={onClose} maxWidth="680px">
      {resultado ? (
        <div style={st.seccion}>
          <CajaOk titulo={`Pago ${resultado.pago?.documento}${resultado.repetida ? ' (ya estaba registrado)' : ''}`}>
            Pagado {fmtImporte(resultado.pago?.importe)} · Saldo con el proveedor {fmtImporte(resultado.saldo_proveedor)}
          </CajaOk>
          <div style={st.pie}><Boton variante="ghost" onClick={onClose}>Cerrar</Boton></div>
        </div>
      ) : (
        <div style={st.seccion}>
          <Campo label="Proveedor"><BuscadorTercero value={tercero} onChange={elegirTercero} rol="proveedor" /></Campo>
          {cuenta && (
            <div>
              <div style={st.label}>Saldo actual: <span style={st.numero}>{fmtImporte(cuenta.saldo)}</span></div>
              {cuenta.pendientes?.length > 0 ? (
                <table style={{ ...st.table, marginTop: space.sm }}>
                  <thead><tr><th style={st.th}></th><th style={st.th}>Documento</th><th style={st.th}>Fecha</th><th style={{ ...st.th, textAlign: 'right' }}>Importe</th><th style={{ ...st.th, textAlign: 'right' }}>Pendiente</th></tr></thead>
                  <tbody>
                    {cuenta.pendientes.map(p => (
                      <tr key={p.id} style={st.trClick} {...hoverFila} onClick={() => toggle(p.id)}>
                        <td style={st.td}><input type="checkbox" checked={ids.has(p.id)} onChange={() => toggle(p.id)} onClick={e => e.stopPropagation()} /></td>
                        <td style={st.tdMono}>{p.documento}</td>
                        <td style={st.tdFecha}>{fecha(p.fecha)}</td>
                        <td style={st.tdImporte}>{fmtImporte(p.importe)}</td>
                        <td style={st.tdImporte}>{fmtImporte(p.pendiente)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p style={{ ...st.info, marginTop: space.sm }}>Sin facturas pendientes: el pago queda a cuenta.</p>}
              <p style={{ ...st.info, marginTop: space.sm }}>Sin elegir facturas, el ERP imputa de la más vieja a la más nueva.</p>
            </div>
          )}
          <div style={st.grid2}>
            <Campo label="Importe"><MoneyInput style={st.input} value={importe} onChange={setImporte} placeholder="" /></Campo>
            <Campo label="Fecha (vacío = hoy)"><CampoFecha style={st.input} value={fechaDoc} onChange={setFechaDoc} /></Campo>
          </div>
          <Campo label="Observaciones"><input style={st.input} value={obs} onChange={e => setObs(e.target.value)} maxLength={250} /></Campo>
          <CajaError>{error}</CajaError>
          <div style={st.pie}>
            <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
            <Boton variante="primary" onClick={enviar} disabled={!tercero || !(parseFloat(importe) > 0) || enviando}>{enviando ? 'Enviando al ERP…' : 'Registrar pago'}</Boton>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ---------------------------------------------------------------- Gasto vario

function GastoModal({ onClose }) {
  const [uuid] = useState(nuevoUuid)
  const [tercero, setTercero] = useState(null)
  const [alta, setAlta] = useState(false)
  const [numero, setNumero] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [importe, setImporte] = useState('')
  const [fechaDoc, setFechaDoc] = useState('')
  const [vence, setVence] = useState('')
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)

  const enviar = async () => {
    setError(''); setEnviando(true)
    try {
      const { data } = await erpApi.gasto({ uuid, tercero_id: tercero.id, numero_proveedor: numero.trim(), descripcion: descripcion.trim(), importe, fecha: fechaDoc || undefined, fecha_vencimiento: vence || undefined })
      setResultado(data)
    } catch (e) { setError(mensajeError(e)) } finally { setEnviando(false) }
  }

  return (
    <Modal title="Gasto vario" onClose={onClose} maxWidth="620px">
      {alta && <ModalAltaTercero rol="proveedor" onClose={() => setAlta(false)} onCreado={t => { setTercero(t); setAlta(false) }} />}
      {resultado ? (
        <div style={st.seccion}>
          <CajaOk titulo={`Gasto ${resultado.factura?.numero_proveedor} registrado${resultado.repetida ? ' (ya estaba)' : ''}`}>
            {fmtImporte(resultado.factura?.total)} · Saldo con el proveedor {fmtImporte(resultado.saldo_proveedor)}. ID {resultado.factura?.id}.
          </CajaOk>
          <div style={st.pie}><Boton variante="ghost" onClick={onClose}>Cerrar</Boton></div>
        </div>
      ) : (
        <div style={st.seccion}>
          <p style={{ ...st.info, margin: 0 }}>Un gasto sin artículos (flete, servicio): suma a la cuenta del proveedor y se paga desde "Pago".</p>
          <Campo label="Proveedor"><BuscadorTercero value={tercero} onChange={setTercero} rol="proveedor" onNuevo={() => setAlta(true)} /></Campo>
          <div style={st.grid2}>
            <Campo label="Número del comprobante"><input style={st.input} value={numero} onChange={e => setNumero(e.target.value)} maxLength={40} /></Campo>
            <Campo label="Importe final"><MoneyInput style={st.input} value={importe} onChange={setImporte} placeholder="" /></Campo>
          </div>
          <Campo label="Descripción"><input style={st.input} value={descripcion} onChange={e => setDescripcion(e.target.value)} maxLength={200} /></Campo>
          <div style={st.grid2}>
            <Campo label="Fecha (vacío = hoy)"><CampoFecha style={st.input} value={fechaDoc} onChange={setFechaDoc} /></Campo>
            <Campo label="Vencimiento"><CampoFecha style={st.input} value={vence} onChange={setVence} /></Campo>
          </div>
          <CajaError>{error}</CajaError>
          <div style={st.pie}>
            <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
            <Boton variante="primary" onClick={enviar} disabled={!tercero || !numero.trim() || !descripcion.trim() || !(parseFloat(importe) > 0) || enviando}>{enviando ? 'Enviando al ERP…' : 'Registrar gasto'}</Boton>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ---------------------------------------------------------------- Anular (recepción / factura / pago)

function AnularModal({ objetivo, onClose }) {
  const [uuid] = useState(nuevoUuid)
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [hecho, setHecho] = useState(false)

  const efecto = { recepcion: 'la mercadería SALE del depósito (aunque quede negativo)', factura: 'su movimiento de cuenta corriente deja de contar y se deshacen las imputaciones', pago: 'la deuda vuelve y las facturas que cancelaba quedan pendientes otra vez' }[objetivo.tipo]

  const anular = async () => {
    setError(''); setEnviando(true)
    try {
      const fn = { recepcion: erpApi.anularRecepcion, factura: erpApi.anularFacturaCompra, pago: erpApi.anularPago }[objetivo.tipo]
      await fn(objetivo.id, { uuid, motivo })
      setHecho(true)
    } catch (e) { setError(mensajeError(e)) } finally { setEnviando(false) }
  }

  return (
    <Modal title={`Anular ${labelOperacion(objetivo.fila.ruta).toLowerCase()} ${objetivo.numero || ''}`} onClose={onClose} maxWidth="560px">
      {hecho ? (
        <div style={st.seccion}>
          <CajaOk titulo="Anulado en el ERP">Quedó marcado como anulado; nada se borró.</CajaOk>
          <div style={st.pie}><Boton variante="ghost" onClick={onClose}>Cerrar</Boton></div>
        </div>
      ) : (
        <div style={st.seccion}>
          <p style={{ ...st.info, margin: 0 }}>Al anular, {efecto}.</p>
          <Campo label="Motivo (obligatorio)"><input style={st.input} value={motivo} onChange={e => setMotivo(e.target.value)} autoFocus maxLength={250} /></Campo>
          <CajaError>{error}</CajaError>
          <div style={st.pie}>
            <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
            <Boton variante="destructive" onClick={anular} disabled={motivo.trim().length < 3 || enviando}>{enviando ? 'Anulando…' : 'Confirmar anulación'}</Boton>
          </div>
        </div>
      )}
    </Modal>
  )
}
